import type { PetStatus, PetWithStatus } from '@shared/domain/petModels';
import { isEgg, isSick, needsCare, calculateLevelThreshold, NFT_IMAGE_VERSION } from '@shared/domain/petHelper';
import { GAME_CONSTANTS } from '@shared/domain/gameConstants';
import { getSpeciesSlug } from './species';
import { SUIKAMON_SPECIES } from '@shared/domain/speciesConfig';
import { translate } from '../i18n/translate';

const MS_PER_HOUR = 3_600_000;

/**
 * Calculate level from XP using linear progression (inverse formula).
 * Threshold formula: (level - 1) * 1000
 * Inverse: level = floor(xp / 1000) + 1
 *
 * This matches the backend calculateLevelFromXp function in game.ts
 *
 * @param xp - Total XP earned
 * @returns Calculated level (minimum 1)
 */
function calculateLevelFromXp(xp: number): number {
  if (xp === 0) return 1;
  return Math.floor(xp / 1000) + 1;
}

const IMAGE_BASE_URL = '/img';

// NFT images must use absolute URLs so they work on external sites (marketplaces, explorers)
// In development: use relative URLs by setting PUBLIC_URL to empty string or localhost
// In production: use the full domain (e.g., https://suikamon.com)
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL ||
  (import.meta.env.DEV ? '' : 'https://suikamon.com');
const NFT_IMAGE_BASE_URL = PUBLIC_URL ? `${PUBLIC_URL}/species` : '/species';

/**
 * Image cache version - increment this to force browser cache refresh
 * Use when updating pet images, backgrounds, or preview images.
 * Sourced from shared/domain/petHelper.ts so the web app and backend
 * (minting, evolution, Telegram bot) always bust caches together.
 */
export const IMAGE_CACHE_VERSION = NFT_IMAGE_VERSION;

/**
 * The shared egg every Suikamon hatches from, cache-busted. Use this, never the bare path:
 * the egg was redrawn in place (2026-10-23) and a bare URL kept the old one in browsers.
 */
export const EGG_IMAGE_SRC = `/species/egg.png?v=${IMAGE_CACHE_VERSION}`;

/**
 * Pet Helper Utilities
 *
 * This module consolidates all pet-related helper functions:
 * - Pet state validation and checking
 * - Evolution logic and XP calculations
 * - Image URL generation
 * - Action tooltips and disabled states
 */

// ============================================================================
// Pet State & Validation
// ============================================================================

/**
 * Determines if a pet is currently an egg (unhatched).
 *
 * A pet is considered an egg if:
 * - stage === 0 (primary check - this is the source of truth)
 *
 * We ignore the is_egg flag because:
 * 1. It's redundant with stage === 0
 * 2. There could be edge cases where the flag is stale
 * 3. The contract uses stage to determine evolution eligibility
 *
 * @param pet - The pet details object
 * @returns true if the pet is an egg (stage 0), false otherwise
 */
export function isPetEgg(pet: PetWithStatus): boolean {
  return pet.status.stage === 0;
}

/**
 * Whether a hatched pet is actively asking for the player's attention right now —
 * abandoned, in poor condition, unhappy, or low on energy. Excludes eggs, resting,
 * and hibernated pets (those aren't "neglected", they're mid-state). Shared by the
 * per-pet card badge and the Play-page "a Suikamon needs you" notification so both
 * surfaces agree on exactly what counts.
 */
export function petNeedsAttention(pet: PetWithStatus): boolean {
  const status = pet.status;
  if (status.resting || status.hibernated || status.is_egg || status.stage === 0) return false;
  return (
    status.is_abandoned ||
    needsCare(status.overall_condition) ||
    status.happiness < GAME_CONSTANTS.MOOD_STAT_LOW_THRESHOLD ||
    status.energy < GAME_CONSTANTS.ENERGY_TIRED_THRESHOLD
  );
}

/**
 * Determines if a pet can perform care actions (feed, train, clean, etc).
 *
 * Actions are disabled when:
 * - Pet is an egg (stage 0)
 * - Pet is hibernated (out of gas)
 *
 * @param pet - The pet details object
 * @returns true if actions are allowed, false otherwise
 */
export function canPerformActions(pet: PetWithStatus): boolean {
  if (isPetEgg(pet)) {
    return false;
  }

  if (pet.status.hibernated) {
    return false;
  }

  return true;
}

/**
 * Determines if a specific action button should be disabled.
 *
 * NOTE: This function only checks game-logic conditions (e.g., pet is resting,
 * not enough energy). Loading state is NOT checked here - buttons remain clickable
 * during API calls and the UI shows non-blocking feedback instead.
 *
 * @param pet - The pet details object
 * @param action - The action type
 * @param options - Additional options (e.g., foodType for feed action)
 * @returns true if the button should be disabled based on game logic
 */
export function isActionDisabled(
  pet: PetWithStatus,
  action: 'feed' | 'train' | 'clean' | 'refill' | 'medicine' | 'rest' | 'wake' | 'tick' | 'evolve',
  options?: { foodType?: number }
): boolean {
  const isEgg = isPetEgg(pet);
  const status = pet.status;

  if (status.hibernated) {
    return true;
  }

  // Eggs cannot perform any actions except evolve
  if (isEgg && action !== 'evolve') {
    return true;
  }

  // Action-specific checks
  switch (action) {
    case 'feed': {
      if (status.resting) return true;
      if (status.hunger >= GAME_CONSTANTS.STAT_MAX) return true;

      // Normal meal (foodType 0) is disabled when hunger > 80
      // Snack cake (foodType 1) is always available (unless resting or hunger at max)
      const foodType = options?.foodType ?? 0;
      if (foodType === 0 && status.hunger > 80) {
        return true;
      }

      return false;
    }

    case 'train':
      return status.energy < GAME_CONSTANTS.ENERGY_COST_TRAIN || isSick(status.overall_condition) || status.resting;

    case 'clean':
      return status.cleanliness >= GAME_CONSTANTS.CLEAN_CLEANLINESS_MAX || status.resting;

    case 'refill': {
      const now = Date.now();
      const timeSinceRefill = status.last_refill_ms ? now - status.last_refill_ms : GAME_CONSTANTS.ENERGY_REFILL_COOLDOWN_MS;
      return status.energy >= GAME_CONSTANTS.REFILL_ENERGY_MINIMUM || status.resting || timeSinceRefill < GAME_CONSTANTS.ENERGY_REFILL_COOLDOWN_MS;
    }

    case 'medicine':
      // Button is hidden when health > 0, so only check resting here
      return status.resting;

    case 'rest':
      return status.resting || status.energy > GAME_CONSTANTS.ENERGY_REST_MAX_THRESHOLD;

    case 'wake':
      return !status.resting;

    case 'tick':
      return false; // Tick is always available for non-eggs

    case 'evolve':
      // Evolve is handled separately with evolveStatus
      return false;

    default:
      return false;
  }
}

/**
 * Gets a human-readable reason why an action is disabled.
 *
 * @param pet - The pet details object
 * @param action - The action type
 * @param options - Additional options (e.g., foodType for feed action)
 * @returns A tooltip message explaining why the action is disabled, or null if enabled
 */
export function getActionDisabledReason(
  pet: PetWithStatus,
  action: 'feed' | 'train' | 'clean' | 'refill' | 'medicine' | 'rest' | 'wake' | 'tick',
  options?: { foodType?: number }
): string | null {
  const isEgg = isPetEgg(pet);
  const status = pet.status;

  if (isEgg) {
    return translate(`care.disabled.egg.${action}`);
  }

  if (status.hibernated) {
    return translate('care.disabled.hibernated');
  }

  switch (action) {
    case 'feed': {
      if (status.resting) return translate('care.disabled.feedResting');
      if (status.hunger >= GAME_CONSTANTS.STAT_MAX) return translate('care.disabled.full');

      const foodType = options?.foodType ?? 0;
      if (foodType === 0 && status.hunger > 80) {
        return translate('care.disabled.tooFullForMeal');
      }

      return null;
    }

    case 'train':
      if (status.resting) return translate('care.disabled.trainResting');
      if (isSick(status.overall_condition)) return translate('care.disabled.trainSick');
      if (status.energy < GAME_CONSTANTS.TRAIN_ENERGY_MINIMUM) return translate('care.disabled.trainEnergy', { energy: GAME_CONSTANTS.TRAIN_ENERGY_MINIMUM });
      return null;

    case 'clean':
      if (status.resting) return translate('care.disabled.cleanResting');
      if (status.cleanliness >= GAME_CONSTANTS.CLEAN_CLEANLINESS_MAX) return translate('care.disabled.alreadyClean');
      return null;

    case 'refill': {
      if (status.resting) return translate('care.disabled.refillResting');
      if (status.energy >= GAME_CONSTANTS.REFILL_ENERGY_MINIMUM) return translate('care.disabled.energyNearlyFull');

      const now = Date.now();
      const timeSinceRefill = status.last_refill_ms ? now - status.last_refill_ms : GAME_CONSTANTS.ENERGY_REFILL_COOLDOWN_MS;
      if (timeSinceRefill < GAME_CONSTANTS.ENERGY_REFILL_COOLDOWN_MS) {
        const hoursRemaining = Math.ceil((GAME_CONSTANTS.ENERGY_REFILL_COOLDOWN_MS - timeSinceRefill) / MS_PER_HOUR);
        return translate('care.disabled.refillCooldown', { hours: hoursRemaining });
      }
      return null;
    }

    case 'medicine':
      // Button is hidden when health > 0, so only check resting here
      if (status.resting) return translate('care.disabled.medicineResting');
      return null;

    case 'rest':
      if (status.resting) return translate('care.disabled.alreadyResting');
      if (status.energy > GAME_CONSTANTS.ENERGY_REST_MAX_THRESHOLD) return translate('care.disabled.energyTooHigh', { max: GAME_CONSTANTS.ENERGY_REST_MAX_THRESHOLD });
      return null;

    case 'wake':
      if (!status.resting) return translate('care.disabled.notResting');
      return null;

    case 'tick':
      return null;

    default:
      return null;
  }
}

/**
 * Gets a tooltip message for an action button.
 *
 * @param pet - The pet details object
 * @param action - The action type
 * @param details - Additional action details (like food type or training bonus)
 * @returns A tooltip message for the button
 */
export function getActionTooltip(
  pet: PetWithStatus,
  action: 'feed' | 'train' | 'clean' | 'refill' | 'medicine' | 'rest' | 'wake' | 'tick',
  details?: { foodType?: number; trainingBonus?: boolean }
): string {
  const disabledReason = getActionDisabledReason(pet, action, { foodType: details?.foodType });
  if (disabledReason) {
    return disabledReason;
  }

  // Default tooltips when enabled
  switch (action) {
    case 'feed':
      // The numbers come from the rules, so a tooltip can never promise what an action does not do.
      return details?.foodType === 0
        ? translate('care.tooltip.meal', {
            hunger: GAME_CONSTANTS.FEED_MEAL_HUNGER_GAIN,
            happiness: GAME_CONSTANTS.FEED_MEAL_HAPPINESS_GAIN,
            health: GAME_CONSTANTS.FEED_MEAL_HEALTH_GAIN,
          })
        : translate('care.tooltip.snack', {
            hunger: GAME_CONSTANTS.FEED_SNACK_HUNGER_GAIN,
            happiness: GAME_CONSTANTS.FEED_SNACK_HAPPINESS_GAIN,
            healthLoss: GAME_CONSTANTS.FEED_SNACK_HEALTH_LOSS,
            cleanlinessLoss: GAME_CONSTANTS.FEED_SNACK_CLEANLINESS_LOSS,
          });

    case 'train':
      return translate('care.tooltip.train', {
        energy: GAME_CONSTANTS.ENERGY_COST_TRAIN,
        happiness: GAME_CONSTANTS.TRAIN_HAPPINESS_GAIN,
        health: GAME_CONSTANTS.TRAIN_HEALTH_GAIN,
      });

    case 'clean':
      return translate('care.tooltip.clean', {
        cleanliness: GAME_CONSTANTS.CLEAN_CLEANLINESS_GAIN,
        happiness: GAME_CONSTANTS.CLEAN_HAPPINESS_GAIN,
      });

    case 'refill':
      return translate('care.tooltip.refill', {
        max: GAME_CONSTANTS.STAT_MAX,
        hours: GAME_CONSTANTS.ENERGY_REFILL_COOLDOWN_MS / MS_PER_HOUR,
      });

    case 'medicine':
      return translate('care.tooltip.medicine', { health: GAME_CONSTANTS.MEDICINE_HEALTH_GAIN, happiness: GAME_CONSTANTS.MEDICINE_HAPPINESS_PENALTY });

    case 'rest':
      return translate('care.tooltip.rest', { max: GAME_CONSTANTS.ENERGY_REST_MAX_THRESHOLD });

    case 'wake':
      return translate('care.tooltip.wake');

    case 'tick':
      return translate('care.tooltip.tick');

    default:
      return '';
  }
}


/**
 * Determines if a pet is in a playable state (not egg, not hibernated).
 *
 * @param pet - The pet details object
 * @returns true if the pet can be interacted with normally
 */
export function isPetPlayable(pet: PetWithStatus): boolean {
  return !isPetEgg(pet) && !pet.status.hibernated;
}

// ============================================================================
// Evolution Logic
// ============================================================================

export type EvolveStatus = {
  canEvolve: boolean;
  requiredXp: number | null;
  currentXp: number;
  stageCapped: boolean;
  reason?: string;
  progress: number;
  evolutionType?: 'level' | 'stage'; // What type of evolution is available
};

function getRequiredLevelForStage(pet: PetWithStatus): number | null {
  const species = SUIKAMON_SPECIES.find(s => s.id === pet.status.species);
  if (!species) {
    console.warn('[Evolution] Species not found:', pet.status.species);
    return null;
  }

  const stage = pet.status.stage;

  // Eggs can always hatch (level 0 required)
  if (stage === 0) {
    return 0;
  }

  // If stage is at max, no further evolution possible
  if (stage >= species.behavior.maxStage) {
    return null;
  }

  // Return the level required for the NEXT stage
  // stageLevels[N] = level required to evolve FROM Stage N TO Stage N+1
  return species.behavior.stageLevels[stage] ?? null;
}


/**
 * Helper to calculate XP progress toward a requirement
 */
function calculateProgress(currentXp: number, requiredXp: number): {
  remaining: number;
  progress: number;
} {
  const remaining = Math.max(0, requiredXp - currentXp);
  const progress = Math.min(1, currentXp / requiredXp);
  return { remaining, progress };
}

export function evaluateEvolveStatus(pet: PetWithStatus): EvolveStatus {
  const currentXp = Math.max(0, Number.isFinite(pet.status.xp) ? pet.status.xp : 0);
  const currentLevel = pet.status.level; // On-chain level (may be outdated)
  const currentStage = pet.status.stage;

  // Max level reached - no more evolution
  if (currentLevel >= 100) {
    return {
      canEvolve: false,
      requiredXp: 0,
      currentXp,
      stageCapped: true,
      reason: translate('progression.evolve.maxLevel'),
      progress: 1,
    };
  }

  // Calculate what level the pet SHOULD be based on earned XP
  // This matches backend evolutionService.ts logic
  const calculatedLevel = calculateLevelFromXp(currentXp);

  // Check both level-up and stage evolution eligibility
  const requiredLevelForStage = getRequiredLevelForStage(pet);
  const nextLevelXpThreshold = calculateLevelThreshold(currentLevel + 1);

  const canLevelUp = currentXp >= nextLevelXpThreshold;
  const canEvolveStage = requiredLevelForStage !== null && calculatedLevel >= requiredLevelForStage;

  // Case 1: Stage is maxed out (can only level up)
  if (requiredLevelForStage === null) {
    const { remaining, progress } = calculateProgress(currentXp, nextLevelXpThreshold);

    if (canLevelUp) {
      return {
        canEvolve: true,
        requiredXp: nextLevelXpThreshold,
        currentXp,
        stageCapped: true,
        reason: translate('progression.evolve.readyLevel', { level: currentLevel + 1 }),
        progress: 1,
        evolutionType: 'level',
      };
    }

    return {
      canEvolve: false,
      requiredXp: nextLevelXpThreshold,
      currentXp,
      stageCapped: true,
      reason: translate('progression.evolve.needsXp', { xp: remaining }),
      progress,
    };
  }

  // Case 2: Stage evolution available (priority over level-up)
  if (canEvolveStage) {
    const isEggStage = isEgg(currentStage);
    return {
      canEvolve: true,
      requiredXp: nextLevelXpThreshold,
      currentXp,
      stageCapped: false,
      reason: isEggStage ? translate('evolution.readyHatch') : translate('evolution.readyStage', { stage: currentStage + 1 }),
      progress: 1,
      evolutionType: 'stage',
    };
  }

  // Case 3: Level-up available (no stage evolution)
  if (canLevelUp) {
    return {
      canEvolve: true,
      requiredXp: nextLevelXpThreshold,
      currentXp,
      stageCapped: false,
      reason: translate('progression.evolve.readyLevel', { level: currentLevel + 1 }),
      progress: 1,
      evolutionType: 'level',
    };
  }

  // Case 4: Not ready for evolution or level-up
  const { remaining, progress } = calculateProgress(currentXp, nextLevelXpThreshold);
  return {
    canEvolve: false,
    requiredXp: nextLevelXpThreshold,
    currentXp,
    stageCapped: false,
    reason: translate('progression.evolve.needsXp', { xp: remaining }),
    progress,
  };
}

// ============================================================================
// Image & Display Helpers
// ============================================================================

/**
 * Gets the image URL for a pet based on its species and stage.
 * Uses absolute URLs for NFT metadata (works on external sites like marketplaces).
 *
 * Naming scheme:
 * - Stage 0 (egg): species/egg.png (generic egg image for all species)
 * - Stage 1+: species/<speciesSlug>-evolution<stage>.png  e.g. bireon-evolution1.png
 * `stage` matches the on-chain `pet.stage` value.
 *
 * @param speciesId - The species ID
 * @param stage - The evolution stage
 * @returns The image URL path (absolute for NFTs, relative for app UI)
 */
export function getPetStageImageUrl(speciesId: number, stage: number): string {
  // Stage 0 (egg) - use generic egg image
  if (stage === 0) {
    return `${NFT_IMAGE_BASE_URL}/egg.png?v=${IMAGE_CACHE_VERSION}`;
  }

  const safeSlug = getSpeciesSlug(speciesId);
  return `${NFT_IMAGE_BASE_URL}/${safeSlug}-evolution${stage}.png?v=${IMAGE_CACHE_VERSION}`;
}

/**
 * Gets the image URL for a pet for UI display (not NFT metadata).
 * Always uses relative URLs for consistency in the app.
 *
 * @param speciesId - The species ID
 * @param stage - The evolution stage
 * @returns The relative image URL path
 */
export function getPetStageImageUrlForUI(speciesId: number, stage: number): string {
  // Stage 0 (egg) - use generic egg image
  if (stage === 0) {
    return `/species/egg.png?v=${IMAGE_CACHE_VERSION}`;
  }

  const safeSlug = getSpeciesSlug(speciesId);
  return `/species/${safeSlug}-evolution${stage}.png?v=${IMAGE_CACHE_VERSION}`;
}

/** Portrait mood variants that have dedicated per-stage art under /species/bystatus. */
export type PetMoodImage = 'sad' | 'tired';

/**
 * Mood-aware variant of {@link getPetStageImageUrlForUI}. When `moodImage` is set (and
 * the pet isn't an egg), returns the matching per-stage mood art; otherwise returns the
 * neutral stage image. Used by the care portrait (sad / tired) and the battle portrait
 * (tired near death). Callers should keep a neutral `onError` fallback so a species
 * without mood art never breaks.
 *
 * Serves from `/species/bystatus/optimized` — the web-ready 500² palette+alpha assets that
 * match the neutral base images. (The flat `/species/bystatus` files are white-background
 * originals and must NOT be used on the transparent pet canvas.)
 */
export function getPetStageMoodImageUrlForUI(
  speciesId: number,
  stage: number,
  moodImage?: PetMoodImage
): string {
  if (!moodImage || stage === 0) {
    return getPetStageImageUrlForUI(speciesId, stage);
  }
  const safeSlug = getSpeciesSlug(speciesId);
  return `/species/bystatus/optimized/${safeSlug}-evolution${stage}-${moodImage}.png?v=${IMAGE_CACHE_VERSION}`;
}

/**
 * Convenience helper for retrieving the image URL for a pet's current stage.
 *
 * Accepts either a PetStatus object or a PetWithStatus to avoid extra plumbing.
 *
 * @param pet - The pet status info
 * @returns The image URL path for the provided pet stage
 */
type StageSource = Pick<PetStatus, 'species' | 'stage'>;
type PetStageInput = StageSource | { status: StageSource };

export function getPetImageUrl(pet: PetStageInput): string {
  const stageInfo = 'status' in pet ? pet.status : pet;
  return getPetStageImageUrl(stageInfo.species, stageInfo.stage);
}

/**
 * Gets the species preview image for adoption and marketing pages.
 * These are simplified images without stage numbers, from the /img folder.
 *
 * @param speciesId - The species ID
 * @returns The species preview image URL path (e.g., /img/bireon.png)
 */
export function getSpeciesPreviewImage(speciesId: number): string {
  const safeSlug = getSpeciesSlug(speciesId);
  return `${IMAGE_BASE_URL}/${safeSlug}.png?v=${IMAGE_CACHE_VERSION}`;
}

/**
 * Gets the background image URL for a pet species.
 * In Vite, files in the public directory are served at the root path.
 *
 * @param speciesId - The species ID
 * @returns The background image URL path
 */
export function getPetBackgroundUrl(speciesId: number): string {
  const safeSlug = getSpeciesSlug(speciesId);
  return `${IMAGE_BASE_URL}/${safeSlug}-background.png?v=${IMAGE_CACHE_VERSION}`;
}

// ============================================================================
// Breeding Helpers
// ============================================================================

/**
 * Determines if a pet is eligible to breed at the current level.
 * Breeding unlocks at milestone levels: 10, 20, 30, etc.
 *
 * @param level - The pet's current level
 * @returns true if the pet can receive a breeding token at this level
 */
export function canBreedAtLevel(level: number): boolean {
  return level >= GAME_CONSTANTS.BREEDING_LEVEL_INTERVAL &&
         level % GAME_CONSTANTS.BREEDING_LEVEL_INTERVAL === 0;
}

/**
 * Gets the next level at which a pet can breed.
 *
 * @param currentLevel - The pet's current level
 * @returns The next breeding level (e.g., 10, 20, 30, etc.)
 */
export function getNextBreedingLevel(currentLevel: number): number {
  const nextMultiple = Math.ceil(currentLevel / GAME_CONSTANTS.BREEDING_LEVEL_INTERVAL) * GAME_CONSTANTS.BREEDING_LEVEL_INTERVAL;
  return Math.max(nextMultiple, GAME_CONSTANTS.BREEDING_LEVEL_INTERVAL);
}

/**
 * Gets a human-readable message about egg discovery eligibility.
 *
 * @param level - The pet's current level
 * @param hasBredAtLevel - Whether the pet has already found an egg at this level
 * @returns A message describing egg discovery status
 */
export function getBreedingMessage(level: number, hasBredAtLevel?: boolean): string {
  if (!canBreedAtLevel(level)) {
    const nextBreedingLevel = getNextBreedingLevel(level);
    return translate('progression.egg.unlocksAt', { level: nextBreedingLevel });
  }

  if (hasBredAtLevel) {
    const nextBreedingLevel = level + GAME_CONSTANTS.BREEDING_LEVEL_INTERVAL;
    return translate('progression.egg.alreadyFound', { level, next: nextBreedingLevel });
  }

  return translate('progression.egg.ready');
}

// ============================================================================
// Rest Mode Energy Calculation
// ============================================================================

/**
 * Returns the current energy level during rest mode.
 *
 * Energy is now updated by the tick system directly in the database,
 * so this function simply returns the stored energy value.
 *
 * @param status - The pet's current status
 * @param _now - Unused (kept for API compatibility)
 * @returns The current energy (0-100)
 */
export function calculateRestingEnergy(status: PetStatus, _now: number = Date.now()): number {
  // Energy is updated by tick system - just return current value from database
  return status.energy;
}

/**
 * Check if a pet is "thriving" - all core stats are above the happiness threshold.
 * When thriving, pets gain happiness passively over time.
 *
 * Thriving condition: health >= 25 AND hunger >= 25 AND energy >= 25
 * - Baby pets: +1.84 happiness/tick (+20/hour)
 * - Adult pets: +0.92 happiness/tick (+10/hour)
 *
 * @param status - The pet's current status
 * @returns true if the pet is thriving (all core stats >= threshold)
 */
export function isPetThriving(status: PetStatus): boolean {
  const threshold = GAME_CONSTANTS.STAT_HAPPY_THRESHOLD;
  return (
    status.health >= threshold &&
    status.hunger >= threshold &&
    status.energy >= threshold
  );
}
